/* Appointments — the booking system, what its services count as, and what its
 * options do.
 *
 * Three things that are one subject: the console only has a book if a scheduling
 * plugin is switched on, the book is only useful if its services are mapped to
 * turn types, and a mapping is only right if the options that change what the
 * work is are accounted for. A salon that reports "the board keeps calling this
 * a Classic Pedicure" is reporting one of the two lists below.
 *
 * The catalogue and the option list are read from the MIRROR — `services`,
 * `service_option_groups`, `service_option_group_options` — which is the
 * console's own copy of the booking system's data, written by the ingest
 * function. So an empty catalogue here is not a mapping problem: it means
 * nothing has ever been ingested for this salon, and the page says so rather
 * than showing an empty list that looks like a fully mapped one.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { ConfirmButton } from '../components/confirm-button.js';

// The plugin ids this build knows the console ships. Not a constraint — the
// field takes anything, because the console resolves a plugin by whatever id is
// in the setting and a newer build may have one this app has never heard of.
var KNOWN_PLUGINS = ['mangomint'];

var Appointments = {
  name: 'AdminAppointments',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var catalogue = Vue.ref([]);
    var mappings = Vue.ref([]);
    var optionGroups = Vue.ref([]);
    var options = Vue.ref([]);
    var rules = Vue.ref([]);
    var loaded = Vue.ref(false);

    var mapFilter = Vue.ref('');
    var unmappedOnly = Vue.ref(false);
    var optionFilter = Vue.ref('');
    var openOptionId = Vue.ref(null);

    var plugins = Vue.computed(function () {
      return store.settingValue('plugins') || {};
    });

    var scheduling = Vue.computed(function () {
      return (plugins.value && plugins.value.scheduling) || { enabled: false, pluginId: null };
    });

    // The whole `plugins` value is rewritten on every change, because it is one
    // jsonb row holding both slots. Merging rather than replacing is what keeps
    // the channel slot alive when the scheduling switch is touched.
    function writeScheduling(patch) {
      var next = Object.assign({}, plugins.value);
      next.scheduling = Object.assign({}, scheduling.value, patch);
      store.setSetting('plugins', next);
    }

    async function load() {
      var id = store.selectedId.value;
      if (!id) return;
      loaded.value = false;

      var cat = await api.rows('services', id, function (q) { return q.order('name'); });
      var map = await api.rows('tt_service_map', id);
      var groups = await api.rows('service_option_groups', id, function (q) {
        return q.order('name');
      });
      var opts = await api.rows('service_option_group_options', id, function (q) {
        return q.order('name');
      });
      var rl = await api.rows('tt_option_rules', id);

      if (cat) catalogue.value = cat;
      if (map) mappings.value = map;
      if (groups) optionGroups.value = groups;
      if (opts) options.value = opts;
      if (rl) rules.value = rl;
      loaded.value = true;
    }

    Vue.watch(store.selectedId, load, { immediate: true });

    /* ---------- the Mangomint write key ----------
     * Into vault through admin-api and never back out, so this page only ever
     * knows whether one is set. What was typed is cleared as soon as it is saved. */
    var writeKey = Vue.ref(null);
    var writeKeyInput = Vue.ref('');

    async function loadWriteKey() {
      var id = store.selectedId.value;
      writeKey.value = null;
      writeKeyInput.value = '';
      if (!id) return;
      var res = await api.call('mangomint.writeKey.status', { tenantId: id });
      if (res) writeKey.value = res;
    }

    Vue.watch(store.selectedId, loadWriteKey, { immediate: true });

    async function saveWriteKey() {
      var res = await api.call('mangomint.writeKey.set', {
        tenantId: store.selectedId.value, key: writeKeyInput.value.trim()
      });
      if (!res) return;
      writeKeyInput.value = '';
      loadWriteKey();
    }

    async function clearWriteKey() {
      var res = await api.call('mangomint.writeKey.clear', { tenantId: store.selectedId.value });
      if (res) loadWriteKey();
    }

    var mapOf = Vue.computed(function () {
      var out = {};
      mappings.value.forEach(function (m) { out[m.mangomint_service_id] = m.tt_service_id; });
      return out;
    });

    var mappedCount = Vue.computed(function () {
      return catalogue.value.filter(function (c) { return mapOf.value[c.id]; }).length;
    });

    var visibleCatalogue = Vue.computed(function () {
      var q = mapFilter.value.trim().toLowerCase();
      return catalogue.value.filter(function (c) {
        if (unmappedOnly.value && mapOf.value[c.id]) return false;
        return !q || String(c.name).toLowerCase().indexOf(q) !== -1 || String(c.id) === q;
      });
    });

    async function setMapping(externalId, serviceId) {
      var id = store.selectedId.value;
      var row = {
        mangomint_service_id: externalId,
        tt_service_id: serviceId || null,
        updated_at: new Date().toISOString()
      };
      var res = await api.writeRow('tt_service_map', id, row, 'tenant_id,mangomint_service_id');
      if (!res) return;
      var existing = mappings.value.find(function (m) {
        return m.mangomint_service_id === externalId;
      });
      if (existing) existing.tt_service_id = row.tt_service_id;
      else mappings.value.push(Object.assign({ tenant_id: id }, row));
      api.log(id, 'mapping.set', { external: externalId, service: row.tt_service_id });
    }

    /* ---------- options ---------- */

    function ruleFor(optionId) {
      return rules.value.find(function (r) { return r.option_id === optionId; }) || null;
    }

    var groupName = Vue.computed(function () {
      var out = {};
      optionGroups.value.forEach(function (g) { out[g.id] = g.name; });
      return out;
    });

    var visibleOptions = Vue.computed(function () {
      var q = optionFilter.value.trim().toLowerCase();
      var byGroup = {};
      options.value.forEach(function (o) {
        var gname = groupName.value[o.option_group_id] || 'Ungrouped';
        if (q && String(o.name).toLowerCase().indexOf(q) === -1 &&
            gname.toLowerCase().indexOf(q) === -1) return;
        (byGroup[gname] || (byGroup[gname] = [])).push(o);
      });
      return Object.keys(byGroup).sort().map(function (name) {
        return { name: name, options: byGroup[name] };
      });
    });

    // The rule row carries the option's name and its group's, denormalised, the
    // same way the console writes it — so a rule still reads as something a
    // person can recognise after the booking system has renamed the option.
    async function writeRule(option, patch) {
      var id = store.selectedId.value;
      var current = ruleFor(option.id) || {};
      var row = {
        option_id: option.id,
        option_name: option.name,
        group_id: option.option_group_id,
        group_name: groupName.value[option.option_group_id] || null,
        tt_service_id: 'tt_service_id' in patch ? patch.tt_service_id : (current.tt_service_id || null),
        staff_ids: 'staff_ids' in patch ? patch.staff_ids : (current.staff_ids || []),
        updated_at: new Date().toISOString()
      };
      var res = await api.writeRow('tt_option_rules', id, row, 'tenant_id,option_id');
      if (!res) return;
      var existing = ruleFor(option.id);
      if (existing) Object.assign(existing, row);
      else rules.value.push(Object.assign({ tenant_id: id }, row));
      api.log(id, 'option-rule.set', { option: option.id, rule: row });
    }

    function toggleRuleStaff(option, staffId) {
      var ids = ((ruleFor(option.id) || {}).staff_ids || []).slice();
      var i = ids.indexOf(staffId);
      if (i === -1) ids.push(staffId); else ids.splice(i, 1);
      writeRule(option, { staff_ids: ids });
    }

    function summarise(option) {
      var r = ruleFor(option.id);
      if (!r || (!r.tt_service_id && !(r.staff_ids || []).length)) return 'No effect';
      var bits = [];
      var svc = store.data.services.find(function (s) { return s.id === r.tt_service_id; });
      if (svc) bits.push('counts as ' + svc.name);
      var n = (r.staff_ids || []).length;
      if (n) bits.push(n === 1 ? '1 technician only' : n + ' technicians only');
      return bits.join(' · ');
    }

    return {
      scheduling: scheduling,
      writeScheduling: writeScheduling,
      knownPlugins: KNOWN_PLUGINS,
      isSet: store.isSet,
      loaded: loaded,
      reload: load,
      catalogue: catalogue,
      visibleCatalogue: visibleCatalogue,
      mapOf: mapOf,
      mappedCount: mappedCount,
      mapFilter: mapFilter,
      unmappedOnly: unmappedOnly,
      setMapping: setMapping,
      services: Vue.computed(function () {
        return store.data.services.filter(function (s) { return s.active !== false; });
      }),
      staff: Vue.computed(function () {
        return store.data.staff.filter(function (s) { return s.active !== false; });
      }),
      optionFilter: optionFilter,
      visibleOptions: visibleOptions,
      openOptionId: openOptionId,
      ruleFor: ruleFor,
      writeRule: writeRule,
      toggleRuleStaff: toggleRuleStaff,
      summarise: summarise,
      writeKey: writeKey,
      writeKeyInput: writeKeyInput,
      saveWriteKey: saveWriteKey,
      clearWriteKey: clearWriteKey,
      stamp: store.stamp,
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Appointments</h2>',
    '    <p class="page__lead">Where the board gets the day&rsquo;s book from, what each booked',
    '      service counts as, and what an option does to it.</p>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Booking system</h3>',
    '      <span class="tag" :class="isSet(\'plugins\') ? \'tag--set\' : \'\'">',
    '        {{ isSet("plugins") ? "set" : "default" }}</span>',
    '    </div>',
    '    <label class="switch">',
    '      <input type="checkbox" :checked="scheduling.enabled"',
    '        @change="writeScheduling({ enabled: $event.target.checked })" />',
    '      <span class="switch__track"><span class="switch__thumb"></span></span>',
    '      <span class="switch__label">Connected</span>',
    '    </label>',
    '    <p class="hint">On, the board gains the appointments pane, the <strong>next</strong>',
    '      clock on every name, and the mapping below. Off, all of it goes &mdash; turns',
    '      already on the board are untouched either way.</p>',
    '    <div class="card__row">',
    '      <label class="field field--grow">',
    '        <span class="field__label">Plugin id</span>',
    '        <input class="input" type="text" spellcheck="false" placeholder="mangomint"',
    '          :value="scheduling.pluginId || \'\'"',
    '          @change="writeScheduling({ pluginId: $event.target.value.trim() || null })" />',
    '      </label>',
    '    </div>',
    '    <p class="hint">This build ships: <code>{{ knownPlugins.join(", ") }}</code>. The field',
    '      takes anything &mdash; the console resolves whatever id is here against the plugins',
    '      it has, so a newer console can be pointed at one this app has never heard of. An id',
    '      nothing matches leaves the salon with the switch on and no book, which the board',
    '      reports as no booking system installed.</p>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Mangomint write key</h3>',
    '      <span v-if="writeKey" class="tag" :class="writeKey.set ? \'tag--set\' : \'\'">',
    '        {{ writeKey.set ? "set" : "not set" }}</span>',
    '    </div>',
    '    <p class="hint">The proxy key that lets the Optimizer and turn order change the',
    '      technician on a booking. Use a <code>reassign</code> key, which can make that one',
    '      change and nothing else. It is kept in vault and can&rsquo;t be read back. Without',
    '      one, nothing is changed in Mangomint. Dry run is separate and stays on until it is',
    '      turned off.</p>',
    '    <p v-if="writeKey && writeKey.set" class="hint">Set {{ stamp(writeKey.updatedAt) }}.</p>',
    '    <p v-if="writeKey && !writeKey.proxyUrlSet" class="hint">This salon has no proxy URL',
    '      yet, so a key here won&rsquo;t do anything until it has one.</p>',
    '    <div class="card__row">',
    '      <label class="field field--grow">',
    '        <span class="field__label">{{ writeKey && writeKey.set ? "Replace the key" : "Key" }}</span>',
    '        <input v-model="writeKeyInput" class="input" type="password" spellcheck="false"',
    '          autocomplete="new-password" />',
    '      </label>',
    '      <button type="button" class="btn btn--primary" :disabled="!writeKeyInput.trim()"',
    '        @click="saveWriteKey">Save</button>',
    '      <confirm-button v-if="writeKey && writeKey.set" label="Clear"',
    '        confirm="Clear: moves stop reaching Mangomint" @confirmed="clearWriteKey" />',
    '    </div>',
    '  </div>',
    '',
    '  <h3 class="page__sub">Service mapping</h3>',
    '  <p class="page__lead">Each service the booking system knows about, and the turn type it',
    '    counts as. Anything unmapped is badged <strong>?</strong> in the appointments pane and',
    '    cannot start a turn of the right weight.</p>',
    '',
    '  <p v-if="!loaded" class="empty">Loading the catalogue…</p>',
    '  <p v-else-if="!catalogue.length" class="empty">Nothing has ever been ingested for this',
    '    salon &mdash; the mirror is empty, so there is no catalogue to map. Check the ingest',
    '    key under <button type="button" class="link-btn" @click="goPage(\'ingest\')">Ingest</button>.</p>',
    '',
    '  <template v-else>',
    '    <div class="card">',
    '      <div class="card__row">',
    '        <input v-model="mapFilter" type="search" class="input" placeholder="Search services" />',
    '        <label class="switch">',
    '          <input type="checkbox" v-model="unmappedOnly" />',
    '          <span class="switch__track"><span class="switch__thumb"></span></span>',
    '          <span class="switch__label">Unmapped only</span>',
    '        </label>',
    '        <button type="button" class="btn btn--ghost btn--sm" @click="reload()">Reload</button>',
    '      </div>',
    '      <div class="meter" :title="mappedCount + \' of \' + catalogue.length + \' mapped\'">',
    '        <span class="meter__bar"><span class="meter__fill"',
    '          :style="{ width: (catalogue.length ? (mappedCount / catalogue.length * 100) : 0) + \'%\' }"></span></span>',
    '        <span class="meter__n">{{ mappedCount }} of {{ catalogue.length }} mapped</span>',
    '      </div>',
    '    </div>',
    '',
    '    <ul class="rows rows--tight">',
    '      <li v-for="c in visibleCatalogue" :key="c.id" class="row row--slim">',
    '        <span class="row__name">{{ c.name }}</span>',
    '        <span class="row__id mono">{{ c.id }}</span>',
    '        <select class="input row__select" :class="{ \'is-none\': !mapOf[c.id] }"',
    '          :value="mapOf[c.id] || \'\'"',
    '          @change="setMapping(c.id, $event.target.value || null)">',
    '          <option value="">Not mapped</option>',
    '          <option v-for="svc in services" :key="svc.id" :value="svc.id">{{ svc.name }}</option>',
    '        </select>',
    '      </li>',
    '    </ul>',
    '  </template>',
    '',
    '  <h3 class="page__sub">Service options</h3>',
    '  <p class="page__lead">An option can change what the work actually is. Give it a turn',
    '    type and any service booked with it counts as that instead; name technicians and only',
    '    they can be given it. Leave both and the option is a note on the booking.</p>',
    '',
    '  <p v-if="loaded && !visibleOptions.length" class="empty">No options have been ingested',
    '    for this salon.</p>',
    '',
    '  <div class="card" v-if="visibleOptions.length">',
    '    <input v-model="optionFilter" type="search" class="input" placeholder="Search options" />',
    '  </div>',
    '',
    '  <div v-for="g in visibleOptions" :key="g.name" class="opt-group">',
    '    <h4 class="opt-group__name">{{ g.name }}</h4>',
    '    <ul class="rows rows--tight">',
    '      <li v-for="o in g.options" :key="o.id" class="opt-row"',
    '        :class="{ \'is-open\': openOptionId === o.id }">',
    '        <button type="button" class="opt-row__head"',
    '          @click="openOptionId = openOptionId === o.id ? null : o.id">',
    '          <span class="row__name">{{ o.name }}</span>',
    '          <span class="opt-row__sum">{{ summarise(o) }}</span>',
    '          <span class="opt-row__edit">{{ openOptionId === o.id ? "Close" : "Change" }}</span>',
    '        </button>',
    '        <div v-if="openOptionId === o.id" class="opt-row__rules">',
    '          <div class="opt-rule">',
    '            <span class="opt-rule__label">Booked with it, the service counts as</span>',
    '            <select class="input" :value="(ruleFor(o.id) || {}).tt_service_id || \'\'"',
    '              @change="writeRule(o, { tt_service_id: $event.target.value || null })">',
    '              <option value="">Whatever it was booked as</option>',
    '              <option v-for="svc in services" :key="svc.id" :value="svc.id">{{ svc.name }}</option>',
    '            </select>',
    '          </div>',
    '          <div class="opt-rule">',
    '            <span class="opt-rule__label">Only these technicians</span>',
    '            <div class="chips">',
    '              <button v-for="s in staff" :key="s.id" type="button" class="chip"',
    '                :class="{ \'chip--on\': ((ruleFor(o.id) || {}).staff_ids || []).indexOf(s.id) !== -1 }"',
    '                @click="toggleRuleStaff(o, s.id)">',
    '                <span class="chip__name">{{ s.name }}</span>',
    '              </button>',
    '            </div>',
    '            <p v-if="!((ruleFor(o.id) || {}).staff_ids || []).length" class="hint">',
    '              Anyone can take it.</p>',
    '          </div>',
    '        </div>',
    '      </li>',
    '    </ul>',
    '  </div>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'appointments',
  name: 'Appointments',
  group: 'Connections',
  page: Appointments
});

export { Appointments };
